import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { Timestamp } from 'firebase/firestore'
import {
  InstallationStatus,
  InstallationNeedsAssignedCompany,
  InstallationNeedsAssignedAt,
  INSTALLATION_NEED_KEYS,
} from '@otw/models/logistics'
import { TransportTruckWithVehicle } from '@/hooks/logistics/useLogistics'
import { useLogistics } from '@/hooks/logistics/useLogistics'
import { TransporterGroup } from '@otw/models'

const NEED_KEYS = INSTALLATION_NEED_KEYS
type NeedKey = (typeof NEED_KEYS)[number]
const NEED_LABELS: Record<NeedKey, string> = {
  anti_theft: 'Anti-theft',
  cameras: 'Cameras',
  backup_sensors: 'Backup sensors',
}

interface EditNeedsDialogProps {
  open: boolean
  onClose: () => void
  truckWithVehicle: TransportTruckWithVehicle | null
  transporterGroups: TransporterGroup[]
}

const EditNeedsDialog: React.FC<EditNeedsDialogProps> = ({
  open,
  onClose,
  truckWithVehicle,
  transporterGroups,
}) => {
  const [status, setStatus] = useState<Partial<InstallationStatus>>({})
  const [assignedCompany, setAssignedCompany] = useState<InstallationNeedsAssignedCompany>({})
  const { updateTransportTruckNeeds, isUpdatingTransportTruckNeeds } = useLogistics()

  const truck = truckWithVehicle?.truck
  const vehicle = truckWithVehicle?.vehicle
  const yearMakeModel = vehicle
    ? `${vehicle.year ?? ''} ${vehicle.make ?? ''} ${vehicle.model ?? ''}`.trim()
    : '—'

  useEffect(() => {
    if (open && truck) {
      setStatus(truck.installation_status ?? {})
      setAssignedCompany(truck.installation_needs_assigned_company ?? {})
    }
  }, [open, truck])

  const handleChange = (field: NeedKey) => (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (checked) {
      setStatus((prev) => ({ ...prev, [field]: 'needed' }))
    } else {
      setStatus((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
      setAssignedCompany((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleAssignCompany = (field: NeedKey, companyId: string) => {
    setAssignedCompany((prev) => ({
      ...prev,
      [field]: companyId || undefined,
    }))
  }

  const hasUnassignedNeed = NEED_KEYS.some(
    (key) => status[key] && !assignedCompany[key]
  )
  const canSave = !hasUnassignedNeed

  const handleSave = async () => {
    if (!truck || !canSave) return
    const prevAssigned = truck.installation_needs_assigned_company ?? {}
    const prevAssignedAt = truck.installation_needs_assigned_at ?? {}
    const nextAssignedAt: InstallationNeedsAssignedAt = {}
    for (const key of NEED_KEYS) {
      if (!status[key]) continue
      const companyId = assignedCompany[key]
      if (!companyId) continue
      const companyChanged = prevAssigned[key] !== companyId
      const wasMissing = !prevAssignedAt[key]
      nextAssignedAt[key] =
        companyChanged || wasMissing ? Timestamp.now() : prevAssignedAt[key]
    }
    await updateTransportTruckNeeds({
      truckId: truck.id,
      installation_status: status,
      installation_needs_assigned_company: assignedCompany,
      installation_needs_assigned_at: nextAssignedAt,
      installation_needs_force_completed: false,
      installation_needs_force_completed_at: null,
      installation_needs_force_completed_by: null,
      installation_needs_hidden: false,
    })
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Edit vehicle needs</Typography>
        <Button startIcon={<CloseIcon />} onClick={onClose} size="small">
          Close
        </Button>
      </DialogTitle>
      <DialogContent dividers>
        {truck && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {yearMakeModel} {truck.vin ? `(${truck.vin.slice(-4)})` : ''}
            </Typography>
          </Box>
        )}
        <FormGroup sx={{ gap: 2 }}>
          {NEED_KEYS.map((key) => (
            <Box key={key} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!status[key]}
                    onChange={handleChange(key)}
                    color="primary"
                  />
                }
                label={NEED_LABELS[key]}
              />
              {status[key] && (
                <FormControl size="small" sx={{ minWidth: 200, ml: 4 }}>
                  <InputLabel>Assign to company</InputLabel>
                  <Select
                    value={assignedCompany[key] ?? ''}
                    label="Assign to company"
                    onChange={(e) => handleAssignCompany(key, e.target.value)}
                  >
                    <MenuItem value="">Any company (with a leg)</MenuItem>
                    {transporterGroups.map((g) => (
                      <MenuItem key={g.id} value={g.id}>
                        {g.name || g.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          ))}
        </FormGroup>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isUpdatingTransportTruckNeeds || !canSave}
          startIcon={isUpdatingTransportTruckNeeds ? <CircularProgress size={16} /> : undefined}
        >
          {isUpdatingTransportTruckNeeds ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditNeedsDialog
